/**
 * Renders a single thread card in the threads list.
 * Displays a thread icon, title, subtitle, and source product icons.
 * Optionally displays a "Daily Brief" CTA button.
 * Invokes a callback on click.
 */
import React, { memo } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import SunsetIcon from '@atlaskit/icon-lab/core/sunset';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { SourceLogoStack } from '../source-logo-stack/SourceLogoStack';
import { ThreadIcon } from '../thread-icon/ThreadIcon';

import messages from './messages';

const styles = cssMap({
	cardWrapper: {
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	staticBody: {
		backgroundColor: 'transparent',
		width: '100%',
		cursor: 'default',
		textAlign: 'start',
	},
	threadContainer: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	sourcesWrapper: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
	},
	actionsRowSpacer: {
		marginLeft: 'auto',
	},
});

interface ThreadCardProps {
	threadId: string;
	title: string;
	subtitle?: string;
	/** Source URLs used to render a static logo stack. Ignored when `renderSources` is provided. */
	sourceUrls?: string[];
	/** Optional custom sources element (e.g. a full Sources component with popup). Takes precedence over `sourceUrls`. */
	renderSources?: React.ReactNode;
	/** Optional custom icon element. When provided, replaces the default ThreadIcon. */
	renderIcon?: React.ReactNode;
	/** Optional callback for the Daily Brief CTA. When provided, renders a "Daily Brief" button on the card. */
	onDailyBriefClick?: (threadId: string, title: string) => void;
	/** Optional callback for the Create project CTA. When provided, renders a "Create project" button. */
	onCreateProjectClick?: (threadId: string, title: string) => void;
	/** When true, renders the Create space button in a disabled state. */
	isCreateProjectDisabled?: boolean;
	/** Optional callback for the Chat CTA. When provided, renders a "Chat" button on the card. */
	onChatClick?: (threadId: string, title: string) => void;
}

/** A card displaying a thread icon, title, subtitle, and source product icons with action buttons. */
const ThreadCardComponent = ({
	threadId,
	title,
	subtitle,
	sourceUrls,
	renderSources,
	renderIcon,
	onDailyBriefClick,
	onCreateProjectClick,
	isCreateProjectDisabled,
	onChatClick,
}: ThreadCardProps): React.JSX.Element => {
	const intl = useIntl();

	const handleDailyBriefClick = () => {
		onDailyBriefClick?.(threadId, title);
	};

	const handleCreateProjectClick = () => {
		onCreateProjectClick?.(threadId, title);
	};

	const handleChatClick = () => {
		onChatClick?.(threadId, title);
	};

	const hasActions =
		onCreateProjectClick != null || onDailyBriefClick != null || onChatClick != null;
	const hasInlineSourceLogos = !renderSources && sourceUrls && sourceUrls.length > 0;
	// When renderSources is provided alongside actions, render sources inline in the actions row
	const hasInlineRenderSources = renderSources != null && hasActions;

	// Card header: only the icon, title, subtitle — rendered inside the Pressable
	const cardHeader = (
		<Stack space="space.100">
			<Flex alignItems="center">
				<Inline space="space.100" alignBlock="center">
					{renderIcon !== undefined ? renderIcon : <ThreadIcon />}
					<Text size="medium" weight="medium">
						{title}
					</Text>
				</Inline>
			</Flex>
			{subtitle ? (
				<Text size="small" color="color.text.subtlest">
					{subtitle}
				</Text>
			) : null}
			{/* Static source logos with no actions — safe to nest since SourceLogoStack is not interactive */}
			{!hasInlineSourceLogos && !hasActions && !renderSources ? (
				<SourceLogoStack sources={sourceUrls ?? []} />
			) : null}
		</Stack>
	);

	// Actions row: rendered OUTSIDE the Pressable to avoid nested interactive elements
	const actionsRow =
		hasInlineSourceLogos || hasInlineRenderSources || hasActions ? (
			<Flex justifyContent="space-between" alignItems="center">
				<Inline space="space.100" alignBlock="center">
					{onChatClick ? (
						<IconButton
							icon={AiChatIcon}
							label={intl.formatMessage(messages.chatAriaLabel, { threadTitle: title })}
							onClick={handleChatClick}
							appearance="default"
							spacing="default"
							testId={`chat-button-${threadId}`}
						/>
					) : null}
					{onDailyBriefClick ? (
						<Button
							iconBefore={SunsetIcon}
							onClick={handleDailyBriefClick}
							appearance="default"
							spacing="default"
							testId={`daily-brief-button-${threadId}`}
						>
							{intl.formatMessage(messages.dailyBriefLabel)}
						</Button>
					) : null}
					{onCreateProjectClick ? (
						<Button
							onClick={handleCreateProjectClick}
							appearance="default"
							spacing="default"
							isDisabled={isCreateProjectDisabled}
							testId={`create-project-button-${threadId}`}
						>
							{intl.formatMessage(messages.createProjectLabel)}
						</Button>
					) : null}
				</Inline>
				{hasInlineRenderSources ? (
					<Box xcss={styles.actionsRowSpacer}>{renderSources}</Box>
				) : hasInlineSourceLogos ? (
					<Box xcss={styles.actionsRowSpacer}>
						<SourceLogoStack sources={sourceUrls ?? []} />
					</Box>
				) : (
					<Box xcss={styles.actionsRowSpacer} />
				)}
			</Flex>
		) : null;

	return (
		<Box xcss={styles.cardWrapper}>
			<Box xcss={styles.threadContainer}>
				<Stack space="space.250">
					<Box xcss={styles.staticBody} testId={`thread-card-${threadId}`}>
						{cardHeader}
					</Box>
					<Box as="span">
						{actionsRow}
						{renderSources && !hasInlineRenderSources ? (
							<Box xcss={styles.sourcesWrapper}>{renderSources}</Box>
						) : null}
					</Box>
				</Stack>
			</Box>
		</Box>
	);
};

export const ThreadCard: React.MemoExoticComponent<
	({
		threadId,
		title,
		subtitle,
		sourceUrls,
		renderSources,
		renderIcon,
		onDailyBriefClick,
		onCreateProjectClick,
		onChatClick,
	}: ThreadCardProps) => React.JSX.Element
> = memo(ThreadCardComponent);
